import {
  AuthScopes,
  ClaimReservationWithTimeslot,
  EditClaimReservationRequest,
  FeatureFlag,
  ResponseError,
} from '@Generated/back-office-client/src'
import { Button, Dialog, DropdownMenu, Flex, IconButton, Text } from '@radix-ui/themes'
import { Cross2Icon, DotsVerticalIcon, Link1Icon, LinkBreak1Icon } from '@radix-ui/react-icons'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@AppState/redux-store'
import { Modal } from '@Components/common/modal'
import { WithPermissions } from '@Components/common/with-permissions'
import { ClaimReservationsServiceAPI } from '@API/data-services/claim-reservations-service'
import { ClaimFormsServiceAPI } from '@API/data-services/claim-forms-service'
import { LinkClaimFormPopup } from './link-claim-form-popup'
import { CancelReservationConfirmation } from './cancel-reservation-dialog'
import { ResendClaimFormPopup } from './resend-claim-form-popup'
import { ReservationInfo } from './reservation-info'
import { useVisitStatus } from './use-visit-status'
import { useTimeslotAvailability } from './use-timeslot-availability'

enum ModalView {
  DETAILS = 'details',
  EDIT = 'edit',
  LINK = 'link',
  RESEND = 'resend',
  CANCEL = 'cancel',
}

const MODAL_TITLES: Record<ModalView, string> = {
  [ModalView.DETAILS]: 'Reservation Details',
  [ModalView.EDIT]: 'Edit Reservation',
  [ModalView.LINK]: 'Link Claim Form',
  [ModalView.RESEND]: 'Resend Claim Form',
  [ModalView.CANCEL]: 'Cancel Reservation',
}

export const ReservationDetails = ({
  onOpenChange,
  reservation,
  claimCenterId,
  refetchGrid,
  onClaimFormClick,
  onReservationUpdated,
  onReservationRemoved,
}: {
  reservation: ClaimReservationWithTimeslot
  claimCenterId: string
  onOpenChange: (open: boolean) => void
  refetchGrid: () => Promise<{ data: { reservations: ClaimReservationWithTimeslot[] } | undefined }>
  onClaimFormClick?: (reservationId: string) => void
  onReservationUpdated: (reservation: ClaimReservationWithTimeslot) => void
  onReservationRemoved: (reservationId: string) => void
}) => {
  const currentUser = useSelector((state: RootState) => state.users.currentUser)
  const claimFormsEnabled = currentUser?.enabledFeatures?.includes(FeatureFlag.claim_forms) ?? false
  const [errorMessage, setErrorMessage] = useState<string>()
  const [notes, setNotes] = useState<string>(reservation.claimReservationDetails.notes ?? '')
  const [phone, setPhone] = useState<string>(reservation.claimReservationDetails.phone ?? '')
  const [email, setEmail] = useState<string>(reservation.claimReservationDetails.email ?? '')
  const [numberOfTickets, setNumberOfTickets] = useState<number>(
    reservation.claimReservationDetails.numberOfTickets,
  )
  const [activeView, setActiveView] = useState<ModalView>(ModalView.DETAILS)

  const visitStatus = useVisitStatus(reservation, onReservationUpdated, setErrorMessage)

  const timeslotAvailability = useTimeslotAvailability(reservation, claimCenterId, numberOfTickets)

  const { mutate: cancelReservation } = useMutation({
    mutationFn: async () => {
      try {
        return await ClaimReservationsServiceAPI.cancelReservation(reservation.id)
      } catch (e) {
        // If we tried to delete a reservation in the past, that's okay, just ignore it
        if (e instanceof ResponseError && e.response.status === 422) {
          setErrorMessage("This reservation is in the past and can't be cancelled")
        } else {
          setErrorMessage('There was an unknown error with cancelling this reservation')
        }
        throw e
      }
    },
    onSuccess: () => {
      onReservationRemoved(reservation.id)
      onOpenChange(false)
    },
  })

  const { mutate: editReservation, isPending: isEditing } = useMutation({
    mutationFn: () => {
      let editRequest: EditClaimReservationRequest
      if (claimFormsEnabled) {
        editRequest = {
          notes: notes || null,
          phone: phone || null,
          email: email || null,
          numberOfTickets,
          timeslotId: timeslotAvailability.hasTimeslotChanged
            ? timeslotAvailability.selectedTimeslotId
            : undefined,
        }
      } else {
        editRequest = { notes: notes || null }
      }
      return ClaimReservationsServiceAPI.editReservation(reservation.id, editRequest)
    },
    onSuccess: () => {
      if (claimFormsEnabled && timeslotAvailability.hasTimeslotChanged) {
        refetchGrid()
      } else if (claimFormsEnabled) {
        onReservationUpdated({
          ...reservation,
          claimReservationDetails: {
            ...reservation.claimReservationDetails,
            notes: notes || null,
            phone: phone || null,
            email: email || null,
            numberOfTickets,
            editedByAgentName: currentUser?.name ?? null,
          },
        })
      } else {
        onReservationUpdated({
          ...reservation,
          claimReservationDetails: {
            ...reservation.claimReservationDetails,
            notes: notes || null,
            editedByAgentName: currentUser?.name ?? null,
          },
        })
      }
      onOpenChange(false)
    },
    onError: (e) => {
      if (e instanceof ResponseError && e.response.status === 422) {
        setErrorMessage("This reservation is in the past and can't be edited")
      } else if (e instanceof ResponseError && e.response.status === 409) {
        setErrorMessage('This timeslot is no longer available. Please select another time.')
      } else {
        setErrorMessage('There was an error saving this reservation.')
      }
    },
  })

  const { mutate: unlinkClaimForm, isPending: isUnlinking } = useMutation({
    mutationFn: async () => {
      await ClaimFormsServiceAPI.unlinkClaimForm(reservation.claimFormId!, reservation.id)
    },
    onSuccess: () => {
      onReservationUpdated({ ...reservation, claimFormId: undefined })
    },
    onError: () => {
      setErrorMessage('Failed to unlink claim form. Please try again.')
    },
  })

  const renderModalContent = () => {
    if (activeView === ModalView.LINK) {
      return (
        <LinkClaimFormPopup
          reservation={reservation}
          onBack={() => setActiveView(ModalView.DETAILS)}
        />
      )
    }

    if (activeView === ModalView.RESEND) {
      return (
        <ResendClaimFormPopup
          reservation={reservation}
          onBack={() => setActiveView(ModalView.DETAILS)}
        />
      )
    }

    if (activeView === ModalView.CANCEL) {
      return (
        <CancelReservationConfirmation
          onCancel={() => cancelReservation()}
          onBack={() => setActiveView(ModalView.DETAILS)}
          errorMessage={errorMessage}
        />
      )
    }

    const isEditMode = activeView === ModalView.EDIT

    return (
      <>
        <Flex gap="3" justify="end">
          <WithPermissions requiredPermissions={AuthScopes.writeclaim_center_reservations}>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger>
                <IconButton variant="ghost" aria-label="Actions">
                  <DotsVerticalIcon height="20" width="20" style={{ color: 'black' }} />
                </IconButton>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content>
                {!isEditMode && (
                  <DropdownMenu.Item onClick={() => setActiveView(ModalView.EDIT)}>
                    Edit
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Item onClick={() => setActiveView(ModalView.CANCEL)}>
                  Delete
                </DropdownMenu.Item>
                {!reservation.claimReservationDetails.isCancelled && (
                  <WithPermissions requiredFeature={FeatureFlag.claim_forms}>
                    <DropdownMenu.Item onClick={() => setActiveView(ModalView.RESEND)}>
                      Resend Claim Form
                    </DropdownMenu.Item>
                  </WithPermissions>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </WithPermissions>

          <Dialog.Close>
            <IconButton variant="ghost" aria-label="Close">
              <Cross2Icon height="20" width="20" style={{ color: 'black' }} />
            </IconButton>
          </Dialog.Close>
        </Flex>

        <Flex gap="3" direction="column" style={{ padding: '18px 48px 48px 48px' }}>
          <ReservationInfo
            reservation={reservation}
            onClaimFormClick={onClaimFormClick}
            isCheckedIn={visitStatus.isCheckedIn}
            isNoShow={visitStatus.isNoShow}
            onCheckedInChange={visitStatus.toggleCheckedIn}
            onNoShowChange={visitStatus.toggleNoShow}
            isEditing={isEditMode}
            claimFormsEnabled={claimFormsEnabled}
            selectedDate={timeslotAvailability.selectedDate}
            onDateChange={timeslotAvailability.setSelectedDate}
            selectedTimeslotId={timeslotAvailability.selectedTimeslotId}
            onTimeslotChange={timeslotAvailability.setSelectedTimeslotId}
            availableTimeslots={timeslotAvailability.availableTimeslots}
            isAvailabilityLoading={timeslotAvailability.isAvailabilityLoading}
            phone={phone}
            onPhoneChange={setPhone}
            email={email}
            onEmailChange={setEmail}
            notes={notes}
            onNotesChange={setNotes}
            numberOfTickets={numberOfTickets}
            onNumberOfTicketsChange={setNumberOfTickets}
            claimFormReceived={reservation.claimFormReceived}
            hasLinkedClaimForm={!!reservation.claimFormId}
            hasTicketCountChanged={
              numberOfTickets !== reservation.claimReservationDetails.numberOfTickets
            }
          />

          {errorMessage && (
            <Flex justify="center">
              <Text weight="bold" color="red">
                {errorMessage}
              </Text>
            </Flex>
          )}

          {isEditMode && (
            <WithPermissions requiredPermissions={AuthScopes.writeclaim_center_reservations}>
              <Flex style={{ justifyContent: 'center' }}>
                <Button
                  style={{ backgroundColor: 'black', color: 'white' }}
                  disabled={
                    timeslotAvailability.isAvailabilityLoading ||
                    isEditing ||
                    (timeslotAvailability.hasTimeslotChanged &&
                      timeslotAvailability.availableTimeslots.length === 0)
                  }
                  onClick={() => editReservation()}>
                  Save
                </Button>
              </Flex>
            </WithPermissions>
          )}

          {!reservation.claimFormReceived && !reservation.claimFormAuthorized ? (
            <WithPermissions
              requiredPermissions={AuthScopes.writeclaim_center_reservations}
              requiredFeature={FeatureFlag.claim_forms}>
              <Flex justify="center" style={{ paddingTop: '8px' }}>
                {reservation.claimFormId ? (
                  <Button
                    variant="ghost"
                    color="red"
                    size="1"
                    loading={isUnlinking}
                    onClick={() => unlinkClaimForm()}>
                    <LinkBreak1Icon />
                    Unlink Claim Form
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    color="red"
                    size="1"
                    onClick={() => setActiveView(ModalView.LINK)}>
                    <Link1Icon />
                    Link Existing Claim Form
                  </Button>
                )}
              </Flex>
            </WithPermissions>
          ) : null}
        </Flex>
      </>
    )
  }

  return (
    <Modal
      onOpenChange={onOpenChange}
      title={MODAL_TITLES[activeView]}
      description={`${reservation.claimReservationDetails.name} - ${reservation.claimReservationDetails.numberOfTickets} Tickets`}
      hideCloseButton>
      {renderModalContent()}
    </Modal>
  )
}
